import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [name] = useState(user?.displayName || '');
  const [email] = useState(user?.email || '');
  const [description, setDescription] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [includeScreenshot] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Use Sentry's official feedback API
      Sentry.captureFeedback({
        name: name || 'Usuario Anónimo',
        email: email,
        message: description,
      }, {
        captureContext: {
          tags: {
            source: 'custom_modal',
            user_id: user?.uid || 'anonymous',
          },
          extra: {
            currentUrl: window.location.href,
            userAgent: navigator.userAgent,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            timestamp: new Date().toISOString(),
          }
        }
      });

      // Show success state briefly before closing
      setIsSuccess(true);

      // Wait 1.5 seconds then reset form and close modal
      setTimeout(() => {
        setDescription('');
        setIsSuccess(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to submit bug report:', error);
      Sentry.captureException(error);
      // Error is logged but no alert shown - modal stays open for retry
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{t('bugReport.title')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Success State */}
        {isSuccess ? (
          <div className="p-6 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('bugReport.success')}</h3>
            <p className="text-gray-600">{t('bugReport.thankYou')}</p>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* Description Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('bugReport.description')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('bugReport.placeholder')}
              required
              rows={4}
              title={t('bugReport.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm placeholder:text-sm"
            />
          </div>

          {/* Info Notice */}
          <div className="bg-blue-50 border border-blue-200 p-3">
            <p className="text-xs text-blue-700">
              <strong>{t('bugReport.infoNotice')}</strong><br />
              {t('bugReport.infoDetails')}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !description.trim()}
              className="flex-1 py-2 px-4 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {isSubmitting ? t('bugReport.submitting') : t('bugReport.submit')}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};
